import React, { useEffect, useState } from "react";
// import "./teamManagement.css";
import "./esapReportContent.css";
import { useDispatch } from "react-redux";
import { toggleToast } from "../../Actions/ToggleToast";
import API_Root from "../../Constants/API_Root";
import { authFetch } from "../../Authentication/authService";

const ESAPManager = ({ selectedTeamId, userEmail, filenames = [] }) => {
  const dispatch = useDispatch();

  const [esapData, setEsapData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [selectedKbFiles, setSelectedKbFiles] = useState([]);
  const [updating, setUpdating] = useState(false);

  //new states ----
  const [expandedRow, setExpandedRow] = useState(null);
  const [reportContent, setReportContent] = useState({});
  const [loadingRow, setLoadingRow] = useState(null);
  const [openJustifications, setOpenJustifications] = useState({});

  const [reviewData, setReviewData] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [savingESAP, setSavingESAP] = useState(false);

  // =========================
  // FETCH ESAP REPORTS
  // =========================
  const fetchESAPReports = async () => {
    if (!selectedTeamId) return;

    setLoading(true);
    try {
      const res = await authFetch(`${API_Root}files/fetch-esap-reports`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ team_id: selectedTeamId }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data?.message);

      setEsapData(data?.reports || []);
      return data?.reports || [];
    } catch (err) {
      dispatch(
        toggleToast({ show: true, type: "error", message: err.message })
      );
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = async (item) => {
    if (expandedRow === item.report_name) {
      setExpandedRow(null);
      return;
    }

    setExpandedRow(item.report_name);

    if (reportContent[item.report_name]) return;

    try {
      setLoadingRow(item.report_name);

      const res = await authFetch(`${API_Root}files/get-esap-report-content`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          report_name: item.report_name,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data?.message || "Failed to fetch report content");
      }

      setReportContent((prev) => ({
        ...prev,
        [item.report_name]: data,
      }));
    } catch (err) {
      dispatch(
        toggleToast({
          show: true,
          type: "error",
          message: err.message,
        })
      );
    } finally {
      setLoadingRow(null);
    }
  };

  const fetchReportContent = async (reportName) => {
    try {
      // setContentLoading(true);

      const res = await authFetch(`${API_Root}files/get-esap-report-content`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          report_name: reportName,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data?.message || "Failed to fetch report content");
      }

      return data;
    } catch (err) {
      dispatch(
        toggleToast({
          show: true,
          type: "error",
          message: err.message,
        })
      );
      return null;
    }
  };

  const toggleJustification = (key) => {
    setOpenJustifications((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  useEffect(() => {
    fetchESAPReports();
  }, [selectedTeamId]);

  // =========================
  // OPEN MODAL
  // =========================
  const handleOpenModal = (item) => {
    if (item.status === "Completed") return;

    setSelectedItem(item);
    setShowModal(true);

    setUploadFiles([]);
    setSelectedKbFiles([]);
  };

  // =========================
  // DOWNLOAD FILE
  // =========================
  const downloadFile = (url, name) => {
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  // =========================
  // UPDATE ESAP
  // =========================
  const handleUpdate = async () => {
    if (!selectedItem) return;

    setUpdating(true);

    const formData = new FormData();
    formData.append("email", userEmail);
    formData.append("esap_file", selectedItem.report_name);

    try {
      uploadFiles.forEach((file) => {
        formData.append("files", file);
      });

      selectedKbFiles.forEach((file) => {
        formData.append("files", file);
      });

      const response = await authFetch(`${API_Root}files/esap-update`, {
        method: "POST",
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.message || "Failed to update ESAP");
      }
      
      setReviewData(data);
      setShowReviewModal(true);
      setShowModal(false);

      dispatch(
        toggleToast({
          show: true,
          type: "success",
          message: "ESAP updated successfully",
        })
      );

      setShowModal(false);
    } catch (err) {
      dispatch(
        toggleToast({
          show: true,
          type: "error",
          message: err.message,
        })
      );
    } finally {
      setUpdating(false);
    }
  };

  const handleReviewChange = (
    index,
    field,
    value
  ) => {
    setReviewData((prev) => ({
      ...prev,
      content: prev.content.map((item, i) =>
        i === index
          ? {
              ...item,
              [field]: value,
            }
          : item
      ),
    }));
  };

  const handleSaveESAP = async () => {
    try {
      setSavingESAP(true);
  
      const res = await authFetch(
        `${API_Root}files/esap-save`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            report_name: reviewData.report_name,
            content: reviewData.content,
          }),
        }
      );
  
      const data = await res.json();
  
      if (!res.ok) {
        throw new Error(
          data?.message || "Failed to save ESAP"
        );
      }
  
      dispatch(
        toggleToast({
          show: true,
          type: "success",
          message: "ESAP saved successfully",
        })
      );
  
      setShowReviewModal(false);
      setReviewData(null);
  
      await fetchESAPReports();
    } catch (err) {
      dispatch(
        toggleToast({
          show: true,
          type: "error",
          message: err.message,
        })
      );
    } finally {
      setSavingESAP(false);
    }
  };

  // =========================
  // FILE HANDLERS (FIXED)
  // =========================
  const handleUploadChange = (e) => {
    const newFiles = Array.from(e.target.files);

    setUploadFiles((prev) => {
      const combined = [...prev, ...newFiles];

      const unique = combined.filter(
        (file, index, self) =>
          index === self.findIndex((f) => f.name === file.name)
      );

      return unique.slice(0, 5);
    });

    e.target.value = null;
  };

  const handleKbSelect = (e) => {
    const value = e.target.value;
    if (!value) return;

    setSelectedKbFiles((prev) => {
      if (prev.includes(value)) return prev;
      if (prev.length >= 5) return prev;
      return [...prev, value];
    });
  };

  const removeKbFile = (file) => {
    setSelectedKbFiles((prev) => prev.filter((f) => f !== file));
  };

  return (
    <div className="tm-wrapper">
      <div className="tm-card">
        <h4>ESAP Manager</h4>

        {loading ? (
          <p className="empty">Loading...</p>
        ) : (
          <table className="tm-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Action</th>
                <th>Status</th>
                <th>Last Updated</th>
                <th></th>
              </tr>
            </thead>

            <tbody>
              {esapData.map((item, index) => (
                <React.Fragment key={item.report_name}>
                  <tr
                    onClick={() => handleRowClick(item)}
                    style={{ cursor: "pointer" }}
                  >
                    <td>{index + 1}</td>
                    <td>{item.report_type}</td>
                    <td>{item.kb_status}</td>
                    <td>
                      {item.updated_at
                        ? new Date(item.updated_at).toLocaleString()
                        : "N/A"}
                    </td>
                    <td>
                      <button
                        className="primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenModal(item);
                        }}
                      >
                        Update
                      </button>
                    </td>
                  </tr>

                  {expandedRow === item.report_name && (
                    <tr className="tm-expanded-row">
                      <td colSpan="5">
                        <div className="esap-content-wrapper">
                          <div className="esap-content-header">
                            <h4>Report Details</h4>

                            <button
                              className="esap-close-btn"
                              onClick={() => setExpandedRow(null)}
                            >
                              ✕ Close
                            </button>
                          </div>
                          {loadingRow === item.report_name ? (
                            <div className="esap-loading">
                              Loading report content...
                            </div>
                          ) : (
                            <div className="esap-report-content">
                              {(
                                reportContent[item.report_name]?.content || []
                              ).map((record, idx) => {
                                const key = `${item.report_name}-${idx}`;

                                return (
                                  <div key={key} className="esap-record-card">
                                    <div
                                      className="esap-record-header"
                                      onClick={() => toggleJustification(key)}
                                    >
                                      <div className="esap-record-left">
                                        <span className="esap-index">
                                          #{record.index}
                                        </span>

                                        <span className="esap-action">
                                          {record.action}
                                        </span>
                                      </div>

                                      <div className="esap-record-right">
                                        <span
                                          className={`esap-status ${
                                            record.status?.includes("Aligned")
                                              ? "aligned"
                                              : "not-aligned"
                                          }`}
                                        >
                                          {record.status}
                                        </span>

                                        <span className="esap-arrow">
                                          {openJustifications[key] ? "−" : "+"}
                                        </span>
                                      </div>
                                    </div>

                                    {openJustifications[key] && (
                                      <div className="esap-record-body">
                                        <div className="esap-section">
                                          <strong>Justification</strong>
                                          <p>{record.justification}</p>
                                        </div>

                                        <div className="esap-section">
                                          <strong>Source Document</strong>
                                          <p>{record.source_documents}</p>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* MODAL */}
      {showModal && (
        <div className="tm-modal-overlay">
          <div className="tm-modal-box">
            <h3>Update ESAP</h3>

            {/* UPLOAD UI */}
            <div className="tm-section">
              <h4>Upload Files</h4>

              <input type="file" multiple onChange={handleUploadChange} />

              <div className="tm-file-preview-list">
                {uploadFiles.map((file, i) => (
                  <div key={i} className="tm-file-chip">
                    {file.name}
                    <span
                      onClick={() =>
                        setUploadFiles((prev) =>
                          prev.filter((_, index) => index !== i)
                        )
                      }
                    >
                      ✕
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="tm-section">
              <h4>Select KB Files</h4>

              <select onChange={handleKbSelect} style={{ width: "100%" }}>
                <option value="">Select file</option>

                {filenames.map((f, i) => (
                  <option key={i} value={f.filename}>
                    {f.filename}
                  </option>
                ))}
              </select>

              <div className="tm-file-preview-list">
                {selectedKbFiles.map((file, i) => (
                  <div key={i} className="tm-file-chip">
                    {file}
                    <span onClick={() => removeKbFile(file)}>✕</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="tm-modal-actions">
              <button
                disabled={
                  updating ||
                  (uploadFiles.length === 0 && selectedKbFiles.length === 0)
                }
                onClick={handleUpdate}
              >
                {updating ? "Updating..." : "Submit"}
              </button>

              <button onClick={() => setShowModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {showReviewModal && reviewData && (
  <div className="tm-modal-overlay">
    <div className="esap-review-modal">

      <div className="review-header">
        <h3>Review ESAP Content</h3>

        <button
          onClick={() => setShowReviewModal(false)}
        >
          ✕
        </button>
      </div>

      <div className="review-content">
        {reviewData.content?.map(
          (item, index) => (
            <div
              key={index}
              className="review-card"
            >
              <div className="review-index">
                Action #{item.index}
              </div>

              <label>
                Action
              </label>

              <textarea
                value={item.action}
                onChange={(e) =>
                  handleReviewChange(
                    index,
                    "action",
                    e.target.value
                  )
                }
              />

              <label>
                Justification
              </label>

              <textarea
                value={item.justification}
                onChange={(e) =>
                  handleReviewChange(
                    index,
                    "justification",
                    e.target.value
                  )
                }
              />

              <label>
                Status
              </label>

              <input
                value={item.status}
                readOnly
              />

              <label>
                Source Documents
              </label>

              <input
                value={
                  item.source_documents
                }
                readOnly
              />
            </div>
          )
        )}
      </div>

      <div className="review-actions">
        <button
          onClick={handleSaveESAP}
          disabled={savingESAP}
        >
          {savingESAP
            ? "Saving..."
            : "Save ESAP"}
        </button>
      </div>
    </div>
  </div>
)}
    </div>
  );
};

export default ESAPManager;
